/**
 * FieldSync Database Schema
 * 
 * Centralized collection definitions and field types.
 * Use this as the single source of truth for your Firestore structure.
 */

export const COLLECTIONS = {
  users: 'users',
  jobs: 'jobs',
  timesheets: 'timesheets',
  photos: 'photos',
  authEvents: 'authEvents',
  signedTimesheets: 'signedTimesheets',
  settings: 'settings',        // ← NEW: per-company configuration
  zohoTokens: 'zohoTokens',    // ← NEW: OAuth tokens (was implicit)
};

export const USER_ROLES = {
  fieldWorker: 'fieldWorker',
  admin: 'admin',
  manager: 'manager',
  pending: 'pending',
};

export const JOB_STATUS = {
  pending: 'pending',
  inProgress: 'inProgress',
  completed: 'completed',
  cancelled: 'cancelled',
};

/**
 * Collection schemas — for documentation and validation reference.
 * These are NOT enforced by Firestore (it's schemaless), but use them
 * as a contract in your code.
 */
export const SCHEMAS = {
  users: {
    uid: 'string',           // Firebase Auth UID (document ID)
    email: 'string',
    displayName: 'string',
    role: 'string',          // 'fieldWorker' | 'admin' | 'manager'
    phone: 'string?',        // optional
    createdAt: 'timestamp',
    updatedAt: 'timestamp',
  },

  jobs: {
    id: 'string',            // auto-generated doc ID
    title: 'string',
    description: 'string?',
    location: 'string?',
    assignedTo: 'string?',     // worker UID
    createdBy: 'string',      // admin/manager UID
    status: 'string',         // 'pending' | 'inProgress' | 'completed' | 'cancelled'
    scheduledDate: 'timestamp?',
    scheduledEndDate: 'timestamp?',   // NEW: for multi-day jobs
    isMultiDay: 'boolean?',           // NEW: true if job spans multiple days
    completedAt: 'timestamp?',
    createdAt: 'timestamp',
    updatedAt: 'timestamp',

    // ─── Zoho Books Integration Fields ───
    zohoCustomerId: 'string?',      // Zoho Books contact/company ID
    companyName: 'string?',           // Company name from Zoho
    clientContact: 'string?',         // Client contact person name
    clientEmail: 'string?',           // Client email
    clientPhone: 'string?',           // Client phone
    primarySiteContact: 'string?',    // Primary site contact person
    siteContactPhone: 'string?',      // Site contact phone
    companyAddress: 'string?',        // Full company address
    jobType: 'string?',               // Type of job/service
    jobReference: 'string?',          // External job reference number
    jobDetails: 'string?',            // Detailed job description from Zoho
    additionalInfo: 'string?',        // Additional notes/instructions
    zohoSynced: 'boolean',            // Whether this job was synced from Zoho
    zohoSyncDate: 'timestamp?',       // Last sync timestamp

    // ─── Job Photos ───
    jobPhotos: 'array?',              // Array of {url, caption, uploadedAt} objects
  },

  timesheets: {
    id: 'string',
    jobId: 'string',
    workerId: 'string',
    startTime: 'timestamp',
    endTime: 'timestamp?',
    hoursWorked: 'number?',
    notes: 'string?',
    approvedBy: 'string?',
    approvedAt: 'timestamp?',
    createdAt: 'timestamp',
    // ─── Location Tracking (GPS) ───
    clockInLocation: {
      lat: 'number',           // latitude
      lng: 'number',           // longitude
      accuracy: 'number?',     // accuracy in meters
      timestamp: 'string',     // ISO timestamp
    },
    clockOutLocation: {
      lat: 'number',
      lng: 'number',
      accuracy: 'number?',
      timestamp: 'string',
    },
  },

  photos: {
    id: 'string',
    jobId: 'string',
    uploadedBy: 'string',     // worker UID
    storagePath: 'string',      // Firebase Storage path
    downloadURL: 'string',
    caption: 'string?',
    uploadedAt: 'timestamp',
  },

  authEvents: {
    id: 'string',
    uid: 'string',
    event: 'string',          // 'login' | 'logout' | 'register'
    timestamp: 'timestamp',
    userAgent: 'string?',
    ip: 'string?',
  },

  // ─── NEW: Company Settings ───
  settings: {
    // Document ID: 'app' (singleton per project)
    companyName: 'string',           // Display name
    companySlug: 'string',           // URL slug (e.g., 'citysurface')
    companyLogo: 'string?',          // URL to logo in Storage
    branding: {
      primaryColor: 'string',
      secondaryColor: 'string?',
      fontFamily: 'string?',
    },
    
    // Zoho Books (company-specific)
    zohoOrgId: 'string?',
    zohoClientId: 'string?',
    zohoDatacenter: 'string?',       // 'eu' | 'com' | 'in' | 'com.cn' | 'com.au'
    zohoSynced: 'boolean',
    zohoLastSync: 'timestamp?',
    
    // Features toggles (per-company)
    features: {
      clockInOutEnabled: 'boolean',
      locationTrackingEnabled: 'boolean', // GPS feature
      zohoIntegrationEnabled: 'boolean',
      multiDayJobsEnabled: 'boolean',
      clientSignatureEnabled: 'boolean',
    },
    
    // Contact info
    supportEmail: 'string?',
    supportPhone: 'string?',
    
    // Job defaults
    defaultJobStartTime: 'string',   // e.g., "08:00"
    defaultWorkHours: 'number',      // e.g., 8
    
    updatedAt: 'timestamp',
    updatedBy: 'string',             // admin UID
  },

  zohoTokens: {
    // Document ID: user UID
    accessToken: 'string',
    refreshToken: 'string',
    expiresAt: 'timestamp',
    updatedAt: 'timestamp',
  },
};

/**
 * Required indexes for common queries.
 * Defined here AND in firestore.indexes.json — keep them in sync!
 */
export const REQUIRED_INDEXES = [
  { collection: 'jobs',       fields: ['assignedTo', 'scheduledDate'],  order: ['asc', 'asc'] },
  { collection: 'jobs',       fields: ['status', 'scheduledDate'],        order: ['asc', 'asc'] },
  { collection: 'timesheets',  fields: ['jobId', 'createdAt'],             order: ['asc', 'desc'] },
  { collection: 'timesheets',  fields: ['workerId', 'createdAt'],          order: ['asc', 'desc'] },
  { collection: 'photos',    fields: ['jobId', 'uploadedAt'],             order: ['asc', 'desc'] },
];

/**
 * Zoho Books field mapping for reference
 */
export const ZOHO_FIELD_MAP = {
  companyName: 'contact_name',           // Zoho contact/company name
  clientContact: 'contact_persons[0].first_name + last_name',
  clientEmail: 'contact_persons[0].email',
  clientPhone: 'contact_persons[0].phone',
  companyAddress: 'billing_address.formatted_address',
  jobType: 'custom_field.Job Type',
  jobReference: 'reference_number',
  jobDetails: 'notes',
};
